import { Component, Input, OnChanges, SimpleChanges, ViewChild, ElementRef, AfterViewInit, OnDestroy, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';

/** Um ponto do gráfico: data (YYYY-MM-DD) e quantidade */
export interface InscricaoDiariaPoint {
  data: string;
  quantidade: number;
}

@Component({
  selector: 'app-inscriptions-chart',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div #chartContainer class="w-full">
      <h3 *ngIf="title" class="text-base font-semibold text-gray-900 mb-4">{{ title }}</h3>

      <div *ngIf="!data?.length" class="flex flex-col items-center justify-center py-8 px-4 empty-state" [style.height.px]="height">
        <svg class="empty-state-svg w-32 h-24 text-gray-300 mb-3" viewBox="0 0 160 128" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M20 108V20h120v88" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="empty-chart-axis"/>
          <path d="M20 88 L45 70 L70 75 L95 45 L120 55 L145 30" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="4 4" opacity="0.6" class="empty-chart-line"/>
          <circle cx="20" cy="88" r="4" fill="currentColor" opacity="0.4" class="empty-dot"/>
          <circle cx="45" cy="70" r="4" fill="currentColor" opacity="0.4" class="empty-dot"/>
          <circle cx="70" cy="75" r="4" fill="currentColor" opacity="0.4" class="empty-dot"/>
          <circle cx="95" cy="45" r="4" fill="currentColor" opacity="0.4" class="empty-dot"/>
          <circle cx="120" cy="55" r="4" fill="currentColor" opacity="0.4" class="empty-dot"/>
          <circle cx="145" cy="30" r="4" fill="currentColor" opacity="0.4" class="empty-dot"/>
        </svg>
        <p class="text-gray-500 text-sm font-medium empty-state-title">Nenhum dado para exibir</p>
        <p class="text-gray-400 text-xs mt-1 empty-state-subtitle">As inscrições diárias aparecerão aqui</p>
      </div>

      <div *ngIf="data?.length && data.length > 0" class="w-full overflow-hidden chart-container">
        <svg [attr.viewBox]="viewBox" class="w-full block" [style.height.px]="height" preserveAspectRatio="none">
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="#4A90E2"/>
              <stop offset="100%" stop-color="#63B3ED"/>
            </linearGradient>
            <filter id="circleShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.15"/>
            </filter>
          </defs>

          <g class="grid">
            <line *ngFor="let tick of yTicks"
              [attr.x1]="padding.left"
              [attr.y1]="getYForValue(tick)"
              [attr.x2]="chartRight"
              [attr.y2]="getYForValue(tick)"
              stroke="#f3f4f6"
              stroke-width="1"
              [attr.stroke-dasharray]="tick === 0 ? '' : '4 4'"
              class="grid-line"
            />
            <line [attr.x1]="padding.left" [attr.y1]="padding.top" [attr.x2]="padding.left" [attr.y2]="chartBottom" stroke="#e5e7eb" stroke-width="1" class="axis-line"/>
          </g>

          <path
            [attr.d]="linePath"
            fill="none"
            stroke="url(#lineGradient)"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            pathLength="1"
            stroke-dasharray="1"
            stroke-dashoffset="1"
            class="chart-line"
          />

          <g *ngFor="let point of data; let i = index" class="chart-point">
            <circle [attr.cx]="getPointX(i)" [attr.cy]="getPointY(point.quantidade)" r="12" fill="transparent" />
            
            <circle
              [attr.cx]="getPointX(i)"
              [attr.cy]="getPointY(point.quantidade)"
              r="5" 
              fill="white"
              stroke="#4A90E2"
              stroke-width="2"
              filter="url(#circleShadow)"
              [style.animation-delay]="(300 + i * 80) + 'ms'"
              class="chart-dot"
            />
            <text
              [attr.x]="getPointX(i)"
              [attr.y]="getPointY(point.quantidade) - 10"
              text-anchor="middle"
              class="chart-value"
              [style.animation-delay]="(400 + i * 80) + 'ms'"
            >{{ point.quantidade }}</text>
          </g>

          <g class="labels-x">
            <text *ngFor="let point of data; let i = index"
              [attr.x]="getPointX(i)"
              [attr.y]="chartBottom + 16"
              text-anchor="middle"
              class="fill-gray-500 text-[10px] chart-x-label"
              [style.animation-delay]="(500 + i * 80) + 'ms'"
            >{{ formatLabelX(point.data) }}</text>
          </g>

          <g class="labels-y">
            <text *ngFor="let tick of yTicks"
              [attr.x]="padding.left - 8"
              [attr.y]="getYForValue(tick) + 3"
              text-anchor="end"
              class="fill-gray-400 text-[10px]"
            >{{ tick }}</text>
          </g>
        </svg>
      </div>
    </div>
  `,
  styles: [`
    .chart-container {
      animation: fadeIn 0.4s ease-out;
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    .grid-line {
      opacity: 0;
      animation: gridIn 0.5s ease-out forwards;
    }
    @keyframes gridIn {
      from { opacity: 0; transform: scaleX(0.9); transform-origin: left; }
      to { opacity: 1; transform: scaleX(1); }
    }
    .axis-line {
      animation: axisIn 0.5s ease-out;
    }
    @keyframes axisIn {
      from { opacity: 0; transform-origin: left bottom; }
      to { opacity: 1; }
    }
    .chart-line {
      animation: drawLine 0.9s ease-out forwards;
    }
    @keyframes drawLine {
      from { stroke-dashoffset: 1; }
      to { stroke-dashoffset: 0; }
    }
    .chart-dot {
      opacity: 0;
      transform-origin: center;
      animation: dotIn 0.35s ease-out forwards;
    }
    @keyframes dotIn {
      from { opacity: 0; transform: scale(0); }
      to { opacity: 1; transform: scale(1); }
    }
    .chart-value {
      font-size: 10px;
      font-weight: 600;
      fill: #1f2937;
      opacity: 0;
      animation: valueIn 0.3s ease-out forwards;
    }
    @keyframes valueIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    .chart-x-label {
      opacity: 0;
      animation: labelIn 0.3s ease-out forwards;
    }
    @keyframes labelIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    /* Estilos empty state mantidos */
    .empty-state { animation: emptyFadeIn 0.5s ease-out; }
    .empty-state-svg { animation: emptySvgIn 0.8s ease-out; }
    .empty-chart-axis { opacity: 0; animation: emptyAxisIn 0.6s ease-out 0.1s forwards; }
    .empty-chart-line { opacity: 0; animation: emptyLineIn 0.8s ease-out 0.3s forwards; }
    .empty-dot { opacity: 0; animation: emptyDotIn 0.4s ease-out forwards; }
    .empty-dot:nth-child(3) { animation-delay: 0.5s; }
    .empty-dot:nth-child(4) { animation-delay: 0.55s; }
    .empty-dot:nth-child(5) { animation-delay: 0.6s; }
    .empty-dot:nth-child(6) { animation-delay: 0.65s; }
    .empty-dot:nth-child(7) { animation-delay: 0.7s; }
    .empty-dot:nth-child(8) { animation-delay: 0.75s; }
    .empty-state-title { opacity: 0; animation: emptyTextIn 0.4s ease-out 0.5s forwards; }
    .empty-state-subtitle { opacity: 0; animation: emptyTextIn 0.4s ease-out 0.6s forwards; }
    @keyframes emptyFadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes emptySvgIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
    @keyframes emptyAxisIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes emptyLineIn { from { opacity: 0; } to { opacity: 0.6; } }
    @keyframes emptyDotIn { from { opacity: 0; transform: scale(0); } to { opacity: 0.4; transform: scale(1); } }
    @keyframes emptyTextIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
  `]
})
export class InscriptionsChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('chartContainer') containerRef!: ElementRef;
  
  @Input() data: InscricaoDiariaPoint[] = [];
  @Input() title = '';
  
  /** * Se width for null (padrão), o gráfico se adapta ao container pai usando ResizeObserver.
   * Se for passado um número, o gráfico terá largura fixa.
   */
  @Input() width: number | null = null;
  @Input() height = 300;

  padding = { top: 30, right: 20, bottom: 40, left: 40 };
  chartRight = 0;
  chartBottom = 0;
  maxY = 0;
  viewBox = '0 0 800 300';
  yTicks: number[] = [];
  linePath = '';

  private currentContainerWidth = 800;
  private resizeObserver: ResizeObserver | null = null;

  constructor(private ngZone: NgZone) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data?.length) {
      this.computeScale();
    }
    if (changes['height'] || changes['width']) {
      this.computeScale();
    }
  }

  ngAfterViewInit(): void {
    // Se não há largura fixa, observar o tamanho do container
    if (this.width === null) {
      this.initResizeObserver();
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private initResizeObserver() {
    if (!this.containerRef) return;

    this.resizeObserver = new ResizeObserver(entries => {
      // Executar dentro do NgZone para garantir que o Angular detecte a mudança e atualize a view
      this.ngZone.run(() => {
        for (const entry of entries) {
          // Usa contentRect.width que dá a largura exata do conteúdo
          const width = entry.contentRect.width;
          if (width > 0 && width !== this.currentContainerWidth) {
            this.currentContainerWidth = width;
            this.computeScale();
          }
        }
      });
    });
    
    this.resizeObserver.observe(this.containerRef.nativeElement);
  }

  private computeScale(): void {
    // Usa width fixo se houver, senão usa largura observada do container
    const effectiveWidth = this.width !== null ? this.width : this.currentContainerWidth;
    
    // Atualiza o viewBox para corresponder exatamente à largura disponível
    this.viewBox = `0 0 ${effectiveWidth} ${this.height}`;
    
    const chartWidth = effectiveWidth - this.padding.left - this.padding.right;
    const chartHeight = this.height - this.padding.top - this.padding.bottom;
    
    this.chartRight = this.padding.left + chartWidth;
    this.chartBottom = this.padding.top + chartHeight;

    this.maxY = Math.max(1, ...this.data.map(p => p.quantidade));
    const step = this.maxY <= 5 ? 1 : this.maxY <= 20 ? 5 : Math.ceil(this.maxY / 5);
    this.yTicks = [];
    for (let v = 0; v <= this.maxY; v += step) {
      this.yTicks.push(v);
    }
    if (this.yTicks[this.yTicks.length - 1] !== this.maxY && this.maxY > 0) {
      this.yTicks.push(this.maxY);
    }

    this.buildLinePath();
  }

  private buildLinePath(): void {
    const n = this.data.length;
    if (n === 0) {
      this.linePath = '';
      return;
    }
    const points: string[] = [];
    for (let i = 0; i < n; i++) {
      const x = this.getPointX(i);
      const y = this.getPointY(this.data[i].quantidade);
      const cmd = i === 0 ? 'M' : 'L';
      points.push(`${cmd} ${x} ${y}`);
    }
    this.linePath = points.join(' ');
  }

  getPointX(index: number): number {
    const effectiveWidth = this.width !== null ? this.width : this.currentContainerWidth;
    const n = this.data.length;
    
    if (n <= 1) return this.padding.left + (this.chartRight - this.padding.left) / 2;
    
    const chartWidth = this.chartRight - this.padding.left;
    return this.padding.left + (index / (n - 1)) * chartWidth;
  }

  getPointY(value: number): number {
    if (this.maxY <= 0) return this.chartBottom;
    const chartHeight = this.chartBottom - this.padding.top;
    const ratio = value / this.maxY;
    return this.padding.top + chartHeight * (1 - ratio);
  }

  getYForValue(value: number): number {
    if (this.maxY <= 0) return this.chartBottom;
    const chartHeight = this.chartBottom - this.padding.top;
    return this.padding.top + chartHeight * (1 - value / this.maxY);
  }

  formatLabelX(dataStr: string): string {
    if (!dataStr) return '';
    const [y, m, d] = dataStr.split('-');
    if (!d) return dataStr;
    return `${d}/${m}`;
  }
}